import React from 'react';
import { Route } from 'react-router-dom';

import { CatalogEntityPage, catalogPlugin } from '@backstage/plugin-catalog';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { Root } from './components/Root/Root';

import { AlertDisplay, OAuthRequestDialog } from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { RequirePermission } from '@backstage/plugin-permission-react';

import { simpleIconsColor } from '@dweber019/backstage-plugin-simple-icons';
import { UnifiedThemeProvider } from '@backstage/theme';
import { dlrTheme } from './theme/dlr';
import { DxIframeStorybookPage } from '@internal/backstage-plugin-dx-iframe';
import './global.css';
import { ToolboxPage } from '@drodil/backstage-plugin-toolbox';
import { OpenFeedbackPage } from '@parsifal-m/backstage-plugin-open-feedback';
import { LandingSignInPage } from './components/SignIn/LandingSignInPage';
import UserManagement from './components/userManagement/UserManagement';
import { IdentityProvider } from '@internal/shared';
import CatalogApiDocs from './components/ApiDocs/CatalogApiDocs';
import SettingsPage from './components/user-settings/SettingsPage';
import {
  dxPortalAdvancedFeaturesPermission,
  dxPortalBasicFeaturesPermission,
} from '@internal/shared/src/permission/opa_permissions';
import { IdTokenFieldExtension } from './scaffolder/idToken';
import { SnackbarProvider } from 'notistack';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { scaffolderTranslations } from './translations/scafolderPage';
import UserRequestsPage from './components/userRequests/UserRequestsPage';
import { UserRequestsPermissionGuard } from './components/userRequests/UserRequestsPermissionGuard';
import { CustomErrorPage } from './components/CustomErrorPage';
import ContactUsPage from './components/ContactUs/ContactUsPage';
import { SystemStatusPage } from './components/SystemStatusPage';
import { LandingPage } from './components/LangingPage/LandingPage';
import { PraxoChatWidget } from './components/PraxoChat';
import ApiDefinitionPage from './components/ApiDocs/ApiDefinitionPage';
import DocsPage from './components/DocsV2/DocsPage';
import { ScaffolderReviewCleanup } from './scaffolder/ScaffolderPageWithReviewCleanup';
import { entityPage } from './components/catalog/EntityPage';

const app = createApp({
  apis,
  __experimentalTranslations: {
    availableLanguages: ['en'],
    resources: [scaffolderTranslations],
  },
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
  },
  components: {
    NotFoundErrorPage: () => (
      <CustomErrorPage status="404" statusMessage="Page not found" />
    ),
    SignInPage: LandingSignInPage,
  },
  themes: [
    {
      id: 'dlr-theme',
      title: 'DLR',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={dlrTheme} children={children} />
      ),
    },
  ],
  icons: simpleIconsColor,
});

const queryClient = new QueryClient();

const routes = (
  <FlatRoutes>
    <Route path="/" element={<LandingPage />} />

    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>

    <Route path="/docs-v2/*" element={<DocsPage />} />

    <Route
      path="/create"
      element={
        <RequirePermission permission={dxPortalAdvancedFeaturesPermission}>
          <IdentityProvider>
            <>
              <ScaffolderPage
                headerOptions={{
                  title: 'Create a new Intake Request',
                  subtitle:
                    'Request integration of your app, API or SSO federation with DXM SSO with a simple form',
                }}
              />
              <ScaffolderReviewCleanup />
            </>
          </IdentityProvider>
        </RequirePermission>
      }
    >
      <ScaffolderFieldExtensions>
        <IdTokenFieldExtension />
      </ScaffolderFieldExtensions>
    </Route>

    <Route
      path="/api-docs"
      element={
        <RequirePermission permission={dxPortalBasicFeaturesPermission}>
          <CatalogApiDocs />
        </RequirePermission>
      }
    />

    <Route
      path="/api-docs/:id"
      element={
        <RequirePermission permission={dxPortalBasicFeaturesPermission}>
          <ApiDefinitionPage />
        </RequirePermission>
      }
    />

    <Route
      path="/settings"
      element={
        <IdentityProvider>
          <UserSettingsPage />
        </IdentityProvider>
      }
    >
      {SettingsPage}
    </Route>

    <Route
      path="/toolbox"
      element={
        <RequirePermission permission={dxPortalAdvancedFeaturesPermission}>
          <ToolboxPage
            toolFilterFunction={tool => tool.category !== 'Backstage'}
          />
        </RequirePermission>
      }
    />

    <Route
      path="/storybook"
      element={
        <RequirePermission permission={dxPortalAdvancedFeaturesPermission}>
          <DxIframeStorybookPage />
        </RequirePermission>
      }
    />

    <Route
      path="/system-status"
      element={
        <RequirePermission permission={dxPortalBasicFeaturesPermission}>
          <SystemStatusPage />
        </RequirePermission>
      }
    />

    <Route
      path="/open-feedback"
      element={
        <RequirePermission permission={dxPortalAdvancedFeaturesPermission}>
          <OpenFeedbackPage />
        </RequirePermission>
      }
    />

    <Route
      path="/user-requests/*"
      element={
        <RequirePermission permission={dxPortalAdvancedFeaturesPermission}>
          <IdentityProvider>
            <UserRequestsPermissionGuard>
              <UserRequestsPage />
            </UserRequestsPermissionGuard>
          </IdentityProvider>
        </RequirePermission>
      }
    />

    <Route
      path="/user-management/*"
      element={
        <RequirePermission permission={dxPortalAdvancedFeaturesPermission}>
          <IdentityProvider>
            <UserManagement />
          </IdentityProvider>
        </RequirePermission>
      }
    />

    <Route
      path="/contact-us"
      element={
        <IdentityProvider>
          <ContactUsPage />
        </IdentityProvider>
      }
    />
  </FlatRoutes>
);

export default app.createRoot(
  <QueryClientProvider client={queryClient}>
    <SnackbarProvider
      maxSnack={3}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      autoHideDuration={3000}
      hideIconVariant
      SnackbarProps={{ style: { zIndex: 3000 } }}
      domRoot={document.getElementById('root') as HTMLElement}
    >
      <AlertDisplay />
      <OAuthRequestDialog />
      <AppRouter>
        <IdentityProvider>
          <Root>{routes}</Root>
          <PraxoChatWidget />
        </IdentityProvider>
      </AppRouter>
    </SnackbarProvider>
  </QueryClientProvider>,
);
